import type { ReactNode } from "react";
import { Button, Description, Dropdown, Label, Tooltip } from "@heroui/react";
import {
  ArrowDownRight, ArrowRight, ArrowUpRight, Circle, Equal, Eye, EyeOff, Layers, Layers3, Magnet, Minus,
  MousePointer2, MoveUpRight, MoveVertical, Redo2, Ruler, Spline, Square, Trash2, TrendingUp, Triangle, Type, Undo2,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { DrawingTool } from "./types";

interface ToolMeta {
  tool: DrawingTool;
  icon: LucideIcon;
  label: string;
  /** What it is for, in a few plain words. */
  hint: string;
  keys?: string;
}

interface ToolGroup {
  id: string;
  icon: LucideIcon;
  label: string;
  tools: ToolMeta[];
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
const ALT = isMac ? "⌥" : "Alt+";

// TradingView-style rail: tools grouped behind a menu per kind.
const GROUPS: ToolGroup[] = [
  {
    id: "lines",
    icon: TrendingUp,
    label: "Lines",
    tools: [
      { tool: "trendline", icon: TrendingUp, label: "Trend line", hint: "Join two swing highs or two swing lows", keys: `${ALT}T` },
      { tool: "horizontal", icon: Minus, label: "Horizontal line", hint: "Mark a price: support, resistance, your stop", keys: `${ALT}H` },
      { tool: "ray", icon: MoveUpRight, label: "Ray", hint: "A trend line that keeps going right" },
      { tool: "extended", icon: Spline, label: "Extended line", hint: "A trend line across the whole chart" },
      { tool: "vertical", icon: MoveVertical, label: "Vertical line", hint: "Mark a moment, e.g. a news release" },
      { tool: "channel", icon: Equal, label: "Parallel channel", hint: "Two parallel lines price moves between" },
    ],
  },
  {
    id: "fib",
    icon: Layers,
    label: "Fibonacci",
    tools: [
      { tool: "fibonacci", icon: Layers, label: "Fib retracement", hint: "Draw a swing; pullbacks often pause at 0.382–0.618", keys: `${ALT}F` },
      { tool: "fibextension", icon: Layers3, label: "Fib extension", hint: "Levels past the swing (1.618, 2.618) for targets" },
    ],
  },
  {
    id: "shapes",
    icon: Square,
    label: "Shapes",
    tools: [
      { tool: "rectangle", icon: Square, label: "Rectangle", hint: "Box a range or a zone", keys: `${ALT}R` },
      { tool: "ellipse", icon: Circle, label: "Ellipse", hint: "Circle a pattern" },
      { tool: "triangle", icon: Triangle, label: "Triangle", hint: "Mark a narrowing pattern" },
      { tool: "arrow", icon: ArrowRight, label: "Arrow", hint: "Point at something" },
    ],
  },
  {
    id: "plan",
    icon: ArrowUpRight,
    label: "Trade plan",
    tools: [
      { tool: "long-position", icon: ArrowUpRight, label: "Buy plan", hint: "Click the entry, then the stop below it: the target goes 2× as far (1:2)" },
      { tool: "short-position", icon: ArrowDownRight, label: "Sell plan", hint: "Click the entry, then the stop above it: the target goes 2× as far (1:2)" },
      { tool: "measure", icon: Ruler, label: "Measure", hint: "Price change, % and bars between two points (not saved)", keys: `${ALT}M` },
    ],
  },
];

const TEXT_TOOL: ToolMeta = { tool: "text", icon: Type, label: "Text note", hint: "Write a note on the chart" };

/** What to click while a tool is armed. */
export function placingHint(tool: DrawingTool): string {
  switch (tool) {
    case "horizontal":
      return "Click a price to place the line";
    case "vertical":
      return "Click a candle to place the line";
    case "text":
      return "Click where the note goes";
    case "long-position":
    case "short-position":
      return "Click the entry, then the stop (where you're wrong). The target goes 2× as far: 1:2";
    case "fibonacci":
    case "fibextension":
      return "Click the swing's start, then its end";
    default:
      return "Click the start, then the end (or drag). Shift keeps 45° angles";
  }
}

function TipButton({
  label,
  active,
  tone = "tool",
  isDisabled,
  onPress,
  children,
}: {
  label: string;
  active?: boolean;
  /** "tool": the armed tool. "toggle": an on/off setting. Both a soft grey when on. */
  tone?: "tool" | "toggle" | "danger";
  isDisabled?: boolean;
  onPress: () => void;
  children: ReactNode;
}) {
  return (
    <Tooltip delay={300}>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        aria-label={label}
        aria-pressed={active}
        isDisabled={isDisabled}
        onPress={onPress}
        className={cn(
          "text-muted hover:text-foreground",
          active && "bg-default text-foreground",
          tone === "danger" && "hover:text-danger",
        )}
      >
        {children}
      </Button>
      <Tooltip.Content placement="right">{label}</Tooltip.Content>
    </Tooltip>
  );
}

function RailGroup({ group, tool, onTool }: { group: ToolGroup; tool: DrawingTool; onTool: (t: DrawingTool) => void }) {
  const current = group.tools.find(t => t.tool === tool);
  const Icon = current?.icon ?? group.icon;
  return (
    <Dropdown>
      <Button
        isIconOnly
        size="sm"
        variant="ghost"
        aria-label={`${group.label} tools`}
        className={cn(
          "text-muted hover:text-foreground",
          current && "bg-default text-foreground",
        )}
      >
        <Icon className="size-4" />
      </Button>
      <Dropdown.Popover placement="right top" className="min-w-64">
        <Dropdown.Menu aria-label={group.label} onAction={key => onTool(key as DrawingTool)}>
          {group.tools.map(t => (
            <Dropdown.Item key={t.tool} id={t.tool} textValue={t.label}>
              <t.icon className="size-4 shrink-0 text-muted" />
              <div className="flex min-w-0 flex-col">
                <Label>{t.label}</Label>
                <Description className="text-xs">{t.hint}</Description>
              </div>
              {t.keys && <span className="num ms-auto shrink-0 pl-3 text-2xs text-muted">{t.keys}</span>}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown.Popover>
    </Dropdown>
  );
}

export interface DrawingRailProps {
  tool: DrawingTool;
  onTool: (t: DrawingTool) => void;
  magnet: boolean;
  onMagnet: (on: boolean) => void;
  hidden: boolean;
  onHidden: (hidden: boolean) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  /** Drawings on this coin (the clear button needs some). */
  count: number;
  onClear: () => void;
  className?: string;
}

/**
 * The drawing tools, down the chart's left edge: pointer, tool menus, text,
 * then magnet / hide / undo / redo / clear.
 */
export function DrawingRail(p: DrawingRailProps) {
  const pick = (t: DrawingTool) => p.onTool(p.tool === t ? "none" : t);
  return (
    <div
      role="toolbar"
      aria-label="Drawing tools"
      aria-orientation="vertical"
      className={cn("flex w-10 shrink-0 flex-col items-center gap-0.5 border-r border-separator py-1.5", p.className)}
    >
      <TipButton label="Pointer: select and move drawings" active={p.tool === "none"} onPress={() => p.onTool("none")}>
        <MousePointer2 className="size-4" />
      </TipButton>
      {GROUPS.map(g => (
        <RailGroup key={g.id} group={g} tool={p.tool} onTool={pick} />
      ))}
      <TipButton label={`${TEXT_TOOL.label}: ${TEXT_TOOL.hint.toLowerCase()}`} active={p.tool === "text"} onPress={() => pick("text")}>
        <Type className="size-4" />
      </TipButton>

      <div className="my-1 w-6 border-t border-separator" />

      <TipButton
        label={p.magnet ? "Magnet on: points snap to the candle's high, low, open or close" : "Magnet off: points go exactly where you click"}
        active={p.magnet}
        tone="toggle"
        onPress={() => p.onMagnet(!p.magnet)}
      >
        <Magnet className="size-4" />
      </TipButton>
      <TipButton
        label={p.hidden ? "Show drawings" : "Hide drawings"}
        active={p.hidden}
        tone="toggle"
        onPress={() => p.onHidden(!p.hidden)}
      >
        {p.hidden ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </TipButton>
      <TipButton label={`Undo (${isMac ? "⌘" : "Ctrl+"}Z)`} isDisabled={!p.canUndo} onPress={p.onUndo}>
        <Undo2 className="size-4" />
      </TipButton>
      <TipButton label={`Redo (${isMac ? "⇧⌘" : "Ctrl+Shift+"}Z)`} isDisabled={!p.canRedo} onPress={p.onRedo}>
        <Redo2 className="size-4" />
      </TipButton>
      <TipButton
        label={p.count > 0 ? `Delete all ${p.count} drawing${p.count === 1 ? "" : "s"} on this coin (undo brings them back)` : "No drawings on this coin"}
        tone="danger"
        isDisabled={p.count === 0}
        onPress={p.onClear}
      >
        <Trash2 className="size-4" />
      </TipButton>
    </div>
  );
}
